'use client';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@ahafood/ui/components/avatar';
import { Button } from '@ahafood/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@ahafood/ui/components/dropdown-menu';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { ChevronDown, LogOut, User } from 'lucide-react';
import { useAuth } from '@/providers/auth-provider';
import { useAuthStore } from '@/store/use-auth-store';

export default function UserButton() {
  const { user, isAuthenticated, isLoading } = useAuthStore();
  const { login, logout } = useAuth();

  if (isLoading) {
    // Skeleton matching the account pill shape (avatar + 2 text lines) so the
    // header doesn't shift when the real button swaps in.
    return (
      <div
        className="flex items-center gap-2 rounded-xl border border-neutral-15 bg-white px-2.5 py-1.5"
        aria-label="Đang tải tài khoản"
      >
        <Skeleton className="size-8 shrink-0 rounded-full" />
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-2.5 w-12 rounded" />
          <Skeleton className="h-3.5 w-24 rounded" />
        </div>
      </div>
    );
  }

  // Not logged in → plain outline button that logs in directly (no dropdown)
  if (!isAuthenticated || !user) {
    return (
      <Button
        variant="outline"
        onClick={() =>
          login().catch((err) => console.error('login failed', err))
        }
        className="h-11 gap-2 rounded-full px-4 font-bold"
      >
        Đăng nhập
      </Button>
    );
  }

  const initial = user.name?.charAt(0).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Tài khoản"
          className="group flex items-center gap-2 rounded-xl border border-neutral-15 bg-white px-2.5 py-1.5 shadow-sm outline-none transition-colors hover:border-primary-30 focus-visible:ring-2 focus-visible:ring-primary-50 data-[state=open]:border-primary-30"
        >
          <span className="grid size-8 shrink-0 place-items-center overflow-hidden rounded-full bg-primary-10 text-primary-50">
            {user.picture ? (
              <Avatar className="size-8">
                <AvatarImage
                  src={user.picture}
                  alt={user.name}
                  className="object-cover"
                />
                <AvatarFallback className="bg-primary-10 text-xs font-bold text-primary-50">
                  {initial ?? <User className="size-4" />}
                </AvatarFallback>
              </Avatar>
            ) : (
              <User className="size-4" />
            )}
          </span>
          <span className="flex min-w-0 flex-col items-start leading-tight">
            <span className="text-[11px] text-neutral-50">Tài khoản</span>
            <span className="max-w-[120px] truncate text-sm font-bold text-neutral-90">
              {user.name ?? 'Người dùng'}
            </span>
          </span>
          <ChevronDown className="size-4 shrink-0 text-neutral-50 transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={10}
        className="overflow-hidden border-neutral-10 p-0 shadow-xl"
      >
        <div className="p-2">
          <Button
            variant="ghost"
            onClick={() => void logout()}
            className="w-full justify-center gap-2 text-red-50 font-bold"
          >
            <LogOut />
            Đăng xuất
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
